
function debounce(fn, delay) {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
    };
}

function dispatchCustomEvent(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

const CartAPI = {
    _headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },

    async changeItem(line, quantity) {
        const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: this._headers,
            body: JSON.stringify({ id: line, quantity }),
        });
        if (!res.ok) throw new Error('Cart change failed');
        return res.json();
    },

    async addItem(variantId, quantity = 1, properties = {}) {
        const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: this._headers,
            body: JSON.stringify({ id: variantId, quantity, properties }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Could not add item to cart');
        }
        return res.json();
    },

    async getRecommendations(productId, limit = 6) {
        const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`;
        const res = await fetch(url, { headers: this._headers });
        if (!res.ok) return { products: [] };
        return res.json();
    },

    async getProductVariants(handle) {
        const res = await fetch(`/products/${handle}.js`, { headers: this._headers });
        if (!res.ok) throw new Error('Could not load product');
        return res.json();
    },


    formatMoney(cents) {
        return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    },
    formatMoneyShort(cents) {
        return Math.round(cents / 100).toLocaleString('en-US') + ' USD';
    },
};

class CartDrawer extends HTMLElement {

    constructor() {
        super();
        this._recommendations = [];
        this._recsOffset = 0;
        this._recsPerPage = 2;
        this._recsProductId = null;
        this._recsLoaded = false;
        this._isMutating = false;
        this._activeSizeCardEl = null;
        this._variantCache = {};
        this.debouncedOnChange = debounce((event) => {
            this._onInputChange(event);
        }, 300);
        this._drag = { active: false, startX: 0, scrollLeft: 0 };
    }

    connectedCallback() {
        const toggleBtn = document.getElementById('cart-drawer-toggle');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this._open();
            });
        }

        document.addEventListener('click', (e) => {
            if (
                e.target.closest('a[href="/cart"]') ||
                e.target.closest('[data-cart-trigger]')
            ) {
                e.preventDefault();
                this._open();
            }

            if (this._activeSizeCardEl) {
                const clickedInsideSizes = e.target.closest('.rec-card__inline-sizes');
                const clickedPlus = e.target.closest('.js-rec-plus');
                
                if (!clickedInsideSizes && !clickedPlus) {
                    this._closeInlineSizePicker();
                }
            }
        });

        this.querySelector('.cart-drawer__close')?.addEventListener('click', () => this._close());

        document.getElementById('cart-drawer-overlay')?.addEventListener('click', () => this._close());
        this.addEventListener('click', (e) => {
            if (e.target.closest('#cart-drawer-continue')) this._close();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (this._activeSizeCardEl) {
                    this._closeInlineSizePicker();
                } else {
                    this._close();
                }
            }
        });

        this.addEventListener('change', this.debouncedOnChange.bind(this));

        this.addEventListener('click', (e) => this._onItemClick(e));

        document.addEventListener('cart:refresh', () => this._refresh());

        this._observeAddToCart();

        this._bindDynamicListeners();
    }

    getSectionsToRender() {
        return [
            {
                id: 'Cart-Drawer',
                section: 'cart-drawer',
                selector: '.cart-drawer',
            },
            {
                id: 'cart-drawer-toggle',
                section: 'cart-bubble',
                selector: '.thb-item-count',
            },
        ];
    }

    getSectionInnerHTML(html, selector) {
        return new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(selector)?.innerHTML ?? '';
    }

    _open() {
        document.body.classList.add('open-cc', 'open-cart');
        this.classList.add('active');
        document.getElementById('cart-drawer-overlay')?.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.focus();

        setTimeout(() => {
            const recsEl = this.querySelector('#cart-drawer-recommendations');
            if (recsEl && !this._recsLoaded) {
                this._fetchAndRenderRecommendations(recsEl.dataset.productId);
            }
            recsEl?.classList.add('active');
        }, 0);

        dispatchCustomEvent('cart-drawer:open');
    }

    _close() {
        document.body.classList.remove('open-cc', 'open-cart');
        this.classList.remove('active');
        document.getElementById('cart-drawer-overlay')?.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        dispatchCustomEvent('cart-drawer:close');
    }

    _onItemClick(e) {
        const decBtn = e.target.closest('.js-qty-dec');
        const incBtn = e.target.closest('.js-qty-inc');
        const removeBtn = e.target.closest('.js-remove, .remove');

        if (decBtn) {
            e.preventDefault();
            const index = decBtn.dataset.index;
            const input = this.querySelector(`.cart-item__qty-input[data-index="${index}"]`);
            if (!input) return;
            const newQty = Math.max(0, parseInt(input.value, 10) - 1);
            this._updateQuantity(index, newQty);
            return;
        }

        if (incBtn) {
            e.preventDefault();
            const index = incBtn.dataset.index;
            const input = this.querySelector(`.cart-item__qty-input[data-index="${index}"]`);
            if (!input) return;
            const newQty = parseInt(input.value, 10) + 1;
            this._updateQuantity(index, newQty);
            return;
        }

        if (removeBtn) {
            e.preventDefault();
            const index = removeBtn.dataset.index;
            this._updateQuantity(index, 0);
        }
    }

    _onInputChange(event) {
        if (!event.target.classList.contains('qty')) return;
        const index = event.target.dataset.index;
        const qty = parseInt(event.target.value, 10);
        if (!isNaN(qty)) this._updateQuantity(index, qty);
    }

    _updateQuantity(line, quantity) {
        if (this._isMutating) return;
        this._isMutating = true;

        this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');
        this.querySelector('.product-recommendations--full')?.classList.remove('active');

        const body = JSON.stringify({
            line,
            quantity,
            sections: this.getSectionsToRender().map((s) => s.section),
            sections_url: window.location.pathname,
        });

        dispatchCustomEvent('line-item:change:start', { quantity });

        fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body,
        })
            .then(async (res) => {
                const data = await res.json();

                if (!res.ok || (data.status && data.status >= 400)) {
                    const msg = data.description || data.message || 'Could not update the cart. Please try again.';
                    this._showError(msg);
                    return;
                }

                if (data.sections) {
                    this._applySectionUpdates(data.sections);
                } else {
                    await this._refresh();
                }

                this._bindDynamicListeners();
                dispatchCustomEvent('line-item:change:end', { quantity, cart: data });

                const newFirstId = String(data.items?.[0]?.product_id ?? '');
                const curId = String(this._recsProductId ?? '');

                if (!data.items?.length) {
                    this._recommendations = [];
                    this._recsLoaded = false;
                    this._recsProductId = null;
                } else if (newFirstId && newFirstId !== curId) {
                    this._recsLoaded = false;
                    this._recsProductId = null;
                    this._recommendations = [];
                    this._fetchAndRenderRecommendations(newFirstId);
                    this.querySelector('.product-recommendations--full')?.classList.add('active');
                } else if (this._recsLoaded && this._recommendations.length) {
                    this.querySelector('.product-recommendations--full')?.classList.add('active');
                }
                this._updateCartCount(data.item_count ?? 0);

                this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
            })
            .catch((err) => {
                console.error('[CartDrawer] updateQuantity error:', err);
                this._showError('Something went wrong. Please try again.');
                this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
            })
            .finally(() => {
                this._isMutating = false;
                this._refresh();
            });
    }

    _showError(message) {
        let toast = this.querySelector('.cd-toast');
        if (!toast) {
            toast = document.createElement('div');
            toast.className = 'cd-toast';
            this.appendChild(toast);
        }
        toast.textContent = message;
        toast.classList.remove('is-visible');
        void toast.offsetWidth;
        toast.classList.add('is-visible');

        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
    }

    _updateCartCount(count) {
        const n = Number(count) || 0;

        let badge = document.querySelector('.cart-item-count');

        if (!badge) {
            const wrapper = document.querySelector('.header-icons a.cart-wrapper');
            if (!wrapper) return;

            badge = document.getElementById('cart-svg-count');
            if (!badge) {
                badge = document.createElement('span');
                badge.id = 'cart-svg-count';
                badge.className = 'cart-item-count';
                wrapper.appendChild(badge);
            }
        }

        badge.textContent = n;
        badge.style.display = n > 0 ? '' : 'none';
    }

    _applySectionUpdates(sections) {
        if (!sections) return;
        this.getSectionsToRender().forEach((section) => {
            const sectionHTML = sections[section.section];
            if (!sectionHTML) return;

            const newInner = this.getSectionInnerHTML(sectionHTML, section.selector);
            const target = document.getElementById(section.id);
            if (!target) return;

            const el = target.querySelector(section.selector) || target;

            const oldRecsTrack = el.querySelector('#cart-recommendations-track')?.innerHTML;
            const recsActive = el.querySelector('.product-recommendations--full')?.classList.contains('active');

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = newInner;
            
            const oldItems = el.querySelectorAll('.cart-drawer__item');
            oldItems.forEach(oldItem => {
                const key = oldItem.getAttribute('data-key');
                if (!key) return;
                const newItem = tempDiv.querySelector(`.cart-drawer__item[data-key="${key}"]`);
                if (!newItem) return;

                const oldPrice = oldItem.querySelector('.cart-item__price');
                const newPrice = newItem.querySelector('.cart-item__price');
                if (oldPrice && newPrice) {
                    newPrice.innerHTML = oldPrice.innerHTML;
                }
            });

            // The Edit button is added by custom.js after each render; carry it over so the
            // actions row (qty / Edit / Remove) doesn't jump while it is re-added.
            // The re-rendered item comes back with the size editor closed, so reset it too.
            el.querySelectorAll('.cart-item[data-key]').forEach(oldItem => {
                const oldEdit = oldItem.querySelector('.cp-ce-edit');
                if (!oldEdit) return;
                const newItem = tempDiv.querySelector(`.cart-item[data-key="${oldItem.getAttribute('data-key')}"]`);
                const newRemove = newItem && newItem.querySelector('.cart-item__remove');
                if (!newRemove || newItem.querySelector('.cp-ce-edit')) return;
                const edit = oldEdit.cloneNode(true);
                edit.classList.remove('is-active');
                edit.textContent = 'Edit';
                newRemove.parentNode.insertBefore(edit, newRemove);
            });

            const newTotal = tempDiv.querySelector('#cart-drawer-total');
            if (newTotal && window.bucksCC) {
                newTotal.style.opacity = '0';
                setTimeout(() => {
                    const activeTotal = document.getElementById('cart-drawer-total');
                    if (activeTotal) activeTotal.style.opacity = '';
                }, 300);
            }

            el.innerHTML = tempDiv.innerHTML;

            if (oldRecsTrack) {
                const newRecsTrack = el.querySelector('#cart-recommendations-track');
                if (newRecsTrack) newRecsTrack.innerHTML = oldRecsTrack;
            }

            if (recsActive) {
                el.querySelector('.product-recommendations--full')?.classList.add('active');
            }

            if (window.bucksCC && typeof window.bucksCC.rerender === 'function') {
                window.bucksCC.rerender();
            }
        });
    }

    _observeAddToCart() {
        if (window.__cartDrawerObserving) return;
        window.__cartDrawerObserving = true;

        const self = this;
        const onAddSuccess = debounce(async () => {
            try {
                await self._refresh();
                self._open();
            } catch (err) {
                console.error('[CartDrawer] add-to-cart refresh error:', err);
            }
        }, 300);

        const origOpen = XMLHttpRequest.prototype.open;
        const origSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function (method, url) {
            this._cdUrl = typeof url === 'string' ? url : '';
            return origOpen.apply(this, arguments);
        };

        XMLHttpRequest.prototype.send = function () {
            const url = this._cdUrl || '';
            if (url.includes('/cart/add')) {
                this.addEventListener('load', function () {
                    if (this.status >= 200 && this.status < 300) {
                        onAddSuccess();
                    }
                });
            }
            return origSend.apply(this, arguments);
        };

        const origFetch = window.fetch;
        window.fetch = function (input, init) {
            const url = typeof input === 'string'
                ? input
                : (input instanceof Request ? input.url : '');

            const promise = origFetch.apply(this, arguments);

            if (url.includes('/cart/add')) {
                promise.then((res) => {
                    if (res.ok) onAddSuccess();
                }).catch(() => { });
            }

            return promise;
        };
    }

    async _refresh() {
        this.querySelector('.product-recommendations--full')?.classList.remove('active');

        const sections = 'cart-drawer,cart-bubble';
        try {
            const res = await fetch(`${window.location.pathname}?sections=${sections}`);
            if (!res.ok) throw new Error(`Section fetch failed (${res.status})`);
            const data = await res.json();
            this._applySectionUpdates(data);
            this._bindDynamicListeners();

            const curId = String(this._recsProductId ?? '');
            const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
            if (cartRes.ok) {
                const cart = await cartRes.json();
                this._updateCartCount(cart.item_count ?? 0);

                const firstProductId = String(cart.items?.[0]?.product_id ?? '');
                
                if (!firstProductId) {
                    this._recommendations = [];
                    this._recsLoaded = false;
                    this._recsProductId = null;
                } else if (firstProductId !== curId) {
                    this._recsLoaded = false;
                    this._recsProductId = null;
                    this._recommendations = [];
                    this._fetchAndRenderRecommendations(firstProductId);
                    this.querySelector('.product-recommendations--full')?.classList.add('active');
                } else if (this._recsLoaded && this._recommendations.length) {
                    this.querySelector('.product-recommendations--full')?.classList.add('active');
                }
            }
        } catch (err) {
            console.error('[CartDrawer] refresh error:', err);
            this._showError('Could not refresh the cart. Please reload the page.');
        }
    }

    async _fetchAndRenderRecommendations(productId) {
        if (!productId || this._recsLoaded) return;

        this._recsProductId = productId;

        try {
            const data = await CartAPI.getRecommendations(productId, 6);
            this._recommendations = data.products || [];
            this._recsOffset = 0;
            this._recsLoaded = true;
            await this._renderRecsCards();
        } catch (err) {
            console.error('[CartDrawer] recommendations error:', err);
        }
    }

    async _renderRecsCards() {
        const track = document.getElementById('cart-recommendations-track');
        if (!track) return;

        if (!this._recommendations.length) {
            document.getElementById('cart-drawer-recommendations')?.classList.add('is-hidden');
            return;
        }

        const subColorMap = {};

        await Promise.all(
            this._recommendations.map(async (product) => {
                if (!this._variantCache[product.handle]) {
                    try {
                        const full = await CartAPI.getProductVariants(product.handle);
                        this._variantCache[product.handle] = full;
                    } catch {
                        this._variantCache[product.handle] = product;
                    }
                }
            })
        );

        track.innerHTML = this._recommendations.map((product) => {
            const cachedProduct = this._variantCache[product.handle] || product;
            const allVariants = cachedProduct?.variants || product.variants || [];
            const singleVariantId = allVariants.length === 1 ? allVariants[0].id : null;
            const firstVariant = allVariants[0];

            const img = product.images?.[1]
                ? product.images[1].replace(/\.(jpg|png|webp|gif)(\?.*)?$/, '_400x.$1$2')
                : (product.images?.[0]
                    ? product.images[0].replace(/\.(jpg|png|webp|gif)(\?.*)?$/, '_400x.$1$2')
                    : null);
            const priceValue = firstVariant ? firstVariant.price : (cachedProduct.price ?? product.price);
            const compareAtPriceValue = firstVariant ? firstVariant.compare_at_price : (cachedProduct.compare_at_price ?? product.compare_at_price);
            let priceHtml = '';
            
            if (priceValue) {
                const regularPrice = CartAPI.formatMoneyShort(priceValue);
                if (compareAtPriceValue && Number(compareAtPriceValue) > Number(priceValue)) {
                    const compareAtPrice = CartAPI.formatMoneyShort(compareAtPriceValue);
                    priceHtml = `<span class="price-item--compare"><s style="color: rgba(18, 18, 18, 0.75); margin-right: 5px;"><span class="money buckscc-money">${compareAtPrice}</span></s></span> <span class="price-item--regular"><span class="money buckscc-money">${regularPrice}</span></span>`;
                } else {
                    priceHtml = `<span class="price-item--regular"><span class="money buckscc-money">${regularPrice}</span></span>`;
                }
            }

            const subColors = subColorMap[product.id] ?? [];
            const swatches = subColors.length
                ? this._buildMetafieldSwatches(subColors)
                : this._buildSwatchDots(product);

            const hasSwatches = subColors.length > 0 || this._hasColorOption(product);

            const sizesHTML = this._buildSizesHTML(product.handle);

            return `
          <div class="rec-card" data-product-id="${product.id}" data-product-handle="${_esc(product.handle)}">
          <div class="rec-card__image-wrap">
            <a class="rec-card__link" href="/products/${_esc(product.handle)}">
                ${img
                    ? `<img class="rec-card__image" src="${_esc(img)}" alt="${_esc(product.title)}" loading="lazy">`
                    : `<div class="rec-card__no-image"></div>`}
            </a>

                <!-- Plus button: hidden by default, shown on hover via CSS -->
                <button
                  class="rec-card__plus-btn js-rec-plus"
                  data-product-handle="${_esc(product.handle)}"
                  ${singleVariantId ? `data-variant-id="${singleVariantId}"` : ''}
                  aria-label="Quick add ${_esc(product.title)}"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>

                <!-- Inline size picker: pre-built, toggled visible via JS -->
                <div class="rec-card__inline-sizes" aria-hidden="true">
                  <div class="rec-card__inline-sizes-inner">
                    ${sizesHTML}
                  </div>
                </div>
              </div>

            <div class="rec-card__info">
              <a class="rec-card__title" href="/products/${_esc(product.handle)}">${_esc(product.title)}</a>
              ${product.product_type ? `<p class="rec-card__collection">${_esc(product.product_type)}</p>` : ''}
              <div class="rec-card__price">${priceHtml}</div>
            </div>

            ${hasSwatches ? `
            <div class="rec-card__swatches">
              ${swatches}
            </div>` : ''}
          </div>`;
        }).join('');

        this._updateRecsPosition();
        this._updateRecsNav();
        this._bindDynamicListeners();
        if (window.bucksCC && typeof window.bucksCC.rerender === 'function') {
            window.bucksCC.rerender();
        }
    }

    _buildSizesHTML(handle) {
        const product = this._variantCache[handle];
        if (!product || !product.variants) return '';

        const sizeIndex = (product.options || []).findIndex((o) => /size/i.test(o));
        const variants = product.variants.map((v) => ({
            id: v.id,
            label: sizeIndex >= 0
                ? v[`option${sizeIndex + 1}`] || v.title
                : (v.title === 'Default Title' ? 'One Size' : v.title),
            available: v.available,
        }));

        return variants.map((v) => `
        <button
          class="rec-card__size-btn${!v.available ? ' is-unavailable' : ''}"
          data-variant-id="${v.id}"
          data-card-handle="${_esc(handle)}"
          ${!v.available ? 'disabled' : ''}
        >${_esc(v.label)}</button>`).join('');
    }

    _hasColorOption(product) {
        return (product.options || []).some((o) => /colou?r/i.test(o));
    }

    _buildMetafieldSwatches(subColors) {
        return subColors.slice(0, 5).map((item, i) => {
            const bg = item.color
                ? _colorToHex(item.color)
                : (item.image ? `url(${_esc(item.image)}) center/cover` : '#e0e0e0');

            const isImage = !item.color && item.image;
            const style = isImage
                ? `background-image:url('${_esc(item.image)}');background-size:cover;background-position:center;`
                : `background:${bg};`;

            return `<span
          class="swatch-dot${i === 0 ? ' active' : ''}"
          style="${style}"
          title="${_esc(item.title)}"
          data-color-title="${_esc(item.title)}"
        ></span>`;
        }).join('');
    }

    _buildSwatchDots(product) {
        const variants = product.variants || [];
        const seen = new Set();
        const colors = [];
        variants.forEach((v) => {
            const val = v.option1 || v.option2;
            if (val && !seen.has(val)) { seen.add(val); colors.push(val); }
        });

        const dots = (colors.length ? colors : []).slice(0, 5);
        if (!dots.length) return '';

        return dots.map((c, i) => {
            const bg = typeof c === 'string' && c.startsWith('#') ? c : _colorToHex(c);
            return `<span class="swatch-dot${i === 0 ? ' active' : ''}" style="background:${bg};" title="${_esc(c)}"></span>`;
        }).join('');
    }


    _onRecsPlusClick(e) {
        const btn = e.target.closest('.js-rec-plus');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();

        if (btn.dataset.variantId) {
            this._onInlineSizeSelect(btn);
            return;
        }

        const card = btn.closest('.rec-card');
        if (this._activeSizeCardEl === card) {
            this._closeInlineSizePicker();
            return;
        }
        this._closeInlineSizePicker();
        this._openInlineSizePicker(card);
    }

    _openInlineSizePicker(card) {
        const pickerEl = card.querySelector('.rec-card__inline-sizes');
        if (!pickerEl) return;

        pickerEl.setAttribute('aria-hidden', 'false');
        pickerEl.classList.add('is-open');
        this._activeSizeCardEl = card;
    }

    _closeInlineSizePicker() {
        if (!this._activeSizeCardEl) return;
        const pickerEl = this._activeSizeCardEl.querySelector('.rec-card__inline-sizes');
        if (pickerEl) {
            pickerEl.classList.remove('is-open');
            pickerEl.setAttribute('aria-hidden', 'true');
        }
        this._activeSizeCardEl = null;
    }

    async _onInlineSizeSelect(btn) {
        if (!btn) return;

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        const origLabel = btn.textContent;
        btn.classList.add('is-loading');
        btn.textContent = '…';

        try {
            await CartAPI.addItem(variantId, 1);
            this._closeInlineSizePicker();
            await this._refresh();
            this._open();
        } catch (err) {
            console.error('[CartDrawer] size add error:', err);
            btn.classList.remove('is-loading');
            btn.textContent = origLabel;
            this._showError(err.message || 'Could not add item. Please try again.');
        }
    }

    _onInlineCloseSizePicker(e) {
        const btn = e.target.closest('.js-inline-size-close');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        this._closeInlineSizePicker();
    }

    _recsScroll(direction) {
        const track = document.getElementById('cart-recommendations-track');
        const card = track ? track.querySelector('.rec-card') : null;
        const cardW = card ? card.offsetWidth + 12 : 160;
        const maxTranslate = track ? Math.max(0, track.scrollWidth - track.parentElement.clientWidth) : 0;
        const maxOffset = Math.ceil(maxTranslate / cardW);
        
        this._recsOffset = Math.min(maxOffset, Math.max(0, this._recsOffset + direction));
        this._updateRecsPosition();
        this._updateRecsNav();
    }

    _updateRecsPosition() {
        const track = document.getElementById('cart-recommendations-track');
        if (!track) return;
        const card = track.querySelector('.rec-card');
        const cardW = card ? card.offsetWidth + 12 : 160;
        
        const maxTranslate = Math.max(0, track.scrollWidth - track.parentElement.clientWidth);
        let targetTranslate = this._recsOffset * cardW;
        if (targetTranslate > maxTranslate) {
            targetTranslate = maxTranslate;
        }
        
        track.style.transform = `translateX(-${targetTranslate}px)`;
    }

    _updateRecsNav() {
        const track = document.getElementById('cart-recommendations-track');
        const card = track ? track.querySelector('.rec-card') : null;
        const cardW = card ? card.offsetWidth + 12 : 160;
        const maxTranslate = track ? Math.max(0, track.scrollWidth - track.parentElement.clientWidth) : 0;
        const maxOffset = Math.ceil(maxTranslate / cardW);
        
        const prev = document.getElementById('recs-prev');
        const next = document.getElementById('recs-next');
        if (prev) prev.disabled = this._recsOffset <= 0;
        if (next) next.disabled = this._recsOffset >= maxOffset || maxTranslate === 0;
    }

    _bindDragScroll(track) {
        if (!track || track._dragBound) return;
        track._dragBound = true;

        const d = this._drag;
        const DRAG_THRESHOLD = 5;

        const onMouseDown = (e) => {
            if (e.button !== 0) return;
            d.active = true;
            d.moved = false; 
            d.startX = e.pageX;
            d.startOffset = this._recsOffset;
            track.style.transition = 'none';
        };

        const onMouseMove = (e) => {
            if (!d.active) return;

            const dx = d.startX - e.pageX;

            if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD) return;

            if (!d.moved) {
                d.moved = true;
                track.classList.add('is-dragging');
            }

            e.preventDefault();

            const card = track.querySelector('.rec-card');
            const cardW = card ? card.offsetWidth + 12 : 160;
            const maxTranslate = Math.max(0, track.scrollWidth - track.parentElement.clientWidth);
            const max = maxTranslate / cardW;

            const rawOffset = d.startOffset + dx / cardW;
            const clamped = Math.min(max, Math.max(0, rawOffset));

            track.style.transform = `translateX(-${clamped * cardW}px)`;
            d.currentOffset = clamped;
        };

        const onMouseUp = () => {
            if (!d.active) return;
            d.active = false;
            track.classList.remove('is-dragging');
            track.style.transition = '';

            if (d.moved && d.currentOffset !== undefined) {
                const card = track.querySelector('.rec-card');
                const cardW = card ? card.offsetWidth + 12 : 160;
                const maxTranslate = Math.max(0, track.scrollWidth - track.parentElement.clientWidth);
                const maxOffset = Math.ceil(maxTranslate / cardW);
                
                this._recsOffset = Math.min(maxOffset, Math.max(0, Math.round(d.currentOffset)));
                this._updateRecsPosition();
                this._updateRecsNav();
                d.currentOffset = undefined;
            }

            setTimeout(() => { d.moved = false; }, 0);
        };

        const onTouchStart = (e) => {
            d.active = true;
            d.moved = false;
            d.startX = e.touches[0].pageX;
            d.startOffset = this._recsOffset;
            track.style.transition = 'none';
        };

        const onTouchMove = (e) => {
            if (!d.active) return;

            const dx = d.startX - e.touches[0].pageX;

            if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
            d.moved = true;

            const card = track.querySelector('.rec-card');
            const cardW = card ? card.offsetWidth + 12 : 160;
            const maxTranslate = Math.max(0, track.scrollWidth - track.parentElement.clientWidth);
            const max = maxTranslate / cardW;

            const rawOffset = d.startOffset + dx / cardW;
            const clamped = Math.min(max, Math.max(0, rawOffset));

            track.style.transform = `translateX(-${clamped * cardW}px)`;
            d.currentOffset = clamped;
        };

        const onTouchEnd = () => {
            if (!d.active) return;
            d.active = false;
            track.style.transition = '';

            if (d.moved && d.currentOffset !== undefined) {
                const card = track.querySelector('.rec-card');
                const cardW = card ? card.offsetWidth + 12 : 160;
                const maxTranslate = Math.max(0, track.scrollWidth - track.parentElement.clientWidth);
                const maxOffset = Math.ceil(maxTranslate / cardW);
                
                this._recsOffset = Math.min(maxOffset, Math.max(0, Math.round(d.currentOffset)));
                this._updateRecsPosition();
                this._updateRecsNav();
                d.currentOffset = undefined;
            }

            setTimeout(() => { d.moved = false; }, 0);
        };

        track.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);

        track.addEventListener('touchstart', onTouchStart, { passive: true });
        track.addEventListener('touchmove', onTouchMove, { passive: true });
        track.addEventListener('touchend', onTouchEnd);
    }

    _notesToggle() {
        const toggle = document.getElementById('order-note-toggle');
        if (!toggle || toggle._noteBound) return;
        toggle._noteBound = true;

        toggle.addEventListener('click', () => {
            toggle.nextElementSibling?.classList.add('active');
        });

        toggle.nextElementSibling?.querySelectorAll('.button, .order-note-toggle__content-overlay')
            .forEach((el) => {
                el.addEventListener('click', () => {
                    toggle.nextElementSibling?.classList.remove('active');
                    this._saveNotes();
                });
            });
    }

    _saveNotes() {
        const noteEl = document.getElementById('mini-cart__notes');
        if (!noteEl) return;
        fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ note: noteEl.value }),
        });
    }

    _bindRemoveButtons() {
        this.querySelectorAll('.remove').forEach((btn) => {
            const fresh = btn.cloneNode(true);
            btn.replaceWith(fresh);
            fresh.addEventListener('click', (e) => {
                e.preventDefault();
                this._updateQuantity(fresh.dataset.index, 0);
            });
        });
    }


    _bindDynamicListeners() {
        const prevBtn = document.getElementById('recs-prev');
        const nextBtn = document.getElementById('recs-next');

        if (prevBtn && !prevBtn._recNavBound) {
            prevBtn._recNavBound = true;
            prevBtn.addEventListener('click', () => this._recsScroll(-1));
        }
        if (nextBtn && !nextBtn._recNavBound) {
            nextBtn._recNavBound = true;
            nextBtn.addEventListener('click', () => this._recsScroll(1));
        }

        const track = document.getElementById('cart-recommendations-track');
        if (track && !track._recTrackBound) {
            track._recTrackBound = true;

            track.addEventListener('click', (e) => {
                if (this._drag.moved) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                const sizeBtn = e.target.closest('.rec-card__size-btn');
                if (sizeBtn && !sizeBtn.disabled) {
                    e.preventDefault();
                    e.stopPropagation();
                    this._onInlineSizeSelect(sizeBtn);
                    return;
                }

                this._onRecsPlusClick(e);

                this._onInlineCloseSizePicker(e);
            });

            this._bindDragScroll(track);
        }

        const closeBtn = this.querySelector('.cart-drawer__close');
        if (closeBtn && !closeBtn._closeBound) {
            closeBtn._closeBound = true;
            closeBtn.addEventListener('click', () => this._close());
        }

        this._notesToggle();

        this._bindRemoveButtons();
    }
}

document.addEventListener("DOMContentLoaded", () => {
    if (!customElements.get('cart-drawer')) {
        customElements.define('cart-drawer', CartDrawer);
    }
})



function _esc(str) {
    if (str == null) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function _colorToHex(name) {
    const map = {
        white: '#fff', black: '#1a1a1a', red: '#e05050',
        blue: '#5070e0', green: '#50a060', pink: '#f0a0b0',
        yellow: '#f0d050', orange: '#f07040', purple: '#9060c0',
        grey: '#909090', gray: '#909090', navy: '#203070',
        ivory: '#f5f0e0', beige: '#e8d8c0', camel: '#c49060',
        tan: '#d4a870', nude: '#d4a888', khaki: '#b0a060',
        olive: '#787830',
    };
    const lower = (name || '').toLowerCase();
    for (const key in map) {
        if (lower.includes(key)) return map[key];
    }
    let h = 0;
    for (let i = 0; i < lower.length; i++) h = (h * 31 + lower.charCodeAt(i)) & 0xffff;
    return `hsl(${h % 360}, 38%, 63%)`;
}